import React from 'react';
import { RefreshCw, Info, TrendingUp, Users, DollarSign, Activity, Target, Layers } from 'lucide-react';
import { KPI, neuCard, cardWrap, neuShadow } from '../crm/crmShared';

/*
 * CEO Dashboard display (Carlos §4.7). Pure presentation over the aggregate
 * payload from reports:ceo:run — counts/%/$ only, no PHI, nothing persisted.
 * Reuses the crmShared neumorphic tile system (KPI / neuCard / cardWrap), NOT
 * the clinical BIDashboard. Real tiles render live values; three PLACEHOLDER
 * tiles render an explicit "Not yet tracked" state (never a misleading $0) when
 * their source flag is false: collected revenue + deposits (Stripe external,
 * invoice/payment stack empty) and sales-activity-by-channel (all interactions
 * are type='other', so no channel breakdown exists yet).
 */

// CRM-sourced figures only. crm_opportunities has NO currency column, so "$" is
// the only thing that can honestly be printed for pipeline value. Do NOT use this
// for the Stripe-derived figures — they carry a real currency.
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Stripe-derived figures. Currency-aware by construction: every other money
// formatter in this repo hard-codes USD (src/pages/StripeReportsPage.js:12 passes
// `currency: 'USD'` to every Stripe total regardless of the real currency), which
// is precisely the bug Card E is not allowed to reproduce. Zero-decimal
// currencies get 0 fraction digits — ¥1,000 is not ¥1,000.00.
const ZERO_DECIMAL_UI = new Set(['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf']);
const moneyIn = (n, currency) => {
  const lower = String(currency || 'usd').toLowerCase();
  const digits = ZERO_DECIMAL_UI.has(lower) ? 0 : 2;
  const amount = Number(n || 0);
  try {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: lower.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  } catch (e) {
    // An unrecognised code (e.g. the 'unknown' bucket for a row with a null
    // currency) must still render a number rather than throwing the tile away.
    return `${lower.toUpperCase()} ${amount.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  }
};

// Why collected revenue is unavailable — the four cases need different responses,
// so they must not collapse into one sentence.
// ⚠ Keep these free of a currency symbol. The placeholder tiles are asserted to
// contain no "$" at all (tests/react/CeoDashboard.test.js), which is the whole
// point of a placeholder: it must be impossible to misread as a figure. Saying
// "not shown as $0.00" would put a dollar sign back on the tile.
const COLLECTED_REASON = {
  no_succeeded_charges: 'Stripe returned no live settled charges at all. Reported as untracked rather than as a zero figure — a genuine zero and an unpopulated integration are indistinguishable from here.',
  no_stripe_credential: 'This environment has no Stripe credential, so collected cash could not be read. An unasked question, not a zero.',
  stripe_error: 'The Stripe read failed. This is a reporting outage, not a zero — retry before drawing any conclusion.',
};

const num = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const sectionTitle = { fontSize: 12, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 };
/*
 * ⚠ MEASURED OVERFLOW, fixed 2026-08-25. At a 1292px viewport — full width, not a
 * narrow edge case — "$35,051.80" spilled 16px past its card (leaf right edge 941
 * vs card right edge 925, card width 327). Three of these at a fixed 22px with
 * gap:16 need ~420px inside ~287px of content box, so it overflowed by
 * construction and would do so at any realistic six-figure forecast.
 *
 * ⚠⚠ NO CLIPPING AND NO ELLIPSIS — Daniel's rule, and it is the right one: a
 * TRUNCATED DOLLAR AMOUNT IS A LIE. "$35,051.8…" reads as a smaller number. So
 * overflow must be solved by FITTING the text, never by hiding part of it.
 *
 * Mechanism, in order of what does the work:
 *   1. The row WRAPS. Three figures that cannot sit side by side stack instead, and
 *      a stacked figure gets the full ~287px — which fits even $1,234,567.89 at
 *      22px. This is what guarantees no overflow at any magnitude.
 *   2. clamp() shrinks the glyphs modestly first, so it degrades by getting a
 *      little smaller before it degrades by rewrapping.
 *   3. nowrap on the number itself, so a figure never breaks mid-amount — a
 *      "$35,051." / "80" across two lines is as misleading as truncation.
 *   4. minWidth: 0 on the flex items, without which a flex child refuses to shrink
 *      below its content width and the clamp cannot take effect.
 * Purely presentational: the VALUE is untouched, only its typography.
 */
const bigMoney = {
  fontSize: 'clamp(16px, 1.25vw + 7px, 22px)',
  fontWeight: 700,
  color: '#2E8B98',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};
// Each figure is its own shrinkable, wrappable cell.
const forecastCell = { flex: '1 1 auto', minWidth: 0 };
const subLabel = { fontSize: 12, color: '#6C757D' };

// Explicit "not yet tracked" placeholder — deliberately NOT a $0 figure.
function NotTracked({ title, reason }) {
  return (
    <div style={{ ...neuCard, flex: '1 1 220px', minWidth: 200, marginBottom: 0, opacity: 0.85 }} data-testid="not-tracked-tile">
      <div style={subLabel}>{title}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#ADB5BD', fontStyle: 'italic', margin: '4px 0 6px' }}>
        Not yet tracked
      </div>
      <div style={{ fontSize: 11, color: '#ADB5BD', lineHeight: 1.4 }}>{reason}</div>
    </div>
  );
}

/*
 * The three metrics registered in routes/_analytics_drilldown.js's METRIC_REGISTRY
 * — and ONLY these three (card F1, 2026-08-25). The keys must match the server
 * registry exactly; a mismatch is refused server-side as UNKNOWN_METRIC rather
 * than guessed at.
 *
 * ⚠ Deliberately NOT drillable: "Collected revenue (Stripe)" — its rows live in
 * Stripe, not the database, so there is nothing local to open; and the weighted
 * forecast — its stage keys do not match production, so most live value falls to
 * DEFAULT and a drill-down would be built onto a known-broken aggregate. Both
 * exclusions are the resolver's own reasoning, restated here so a future edit does
 * not "complete the set" by adding a tile the server will refuse.
 */
const DRILLABLE = Object.freeze({
  openOpps: { key: 'ceo.open_opportunities', title: 'Open opportunities' },
  openValue: { key: 'ceo.open_pipeline_value', title: 'Open pipeline value' },
  bookedWon: { key: 'ceo.booked_won_value', title: 'Booked (won) pipeline value' },
});

export default function CeoDashboard({ data, onRefresh, loading, onDrilldown }) {
  if (!data) return null;
  // Clickability is opt-in and driven by the CONTAINER, which knows whether the
  // signed-in user holds analytics:drilldown. Without the prop every tile renders
  // exactly as it did before this card — no hint, no cursor, no role.
  const drill = (m) => (typeof onDrilldown === 'function' ? () => onDrilldown(DRILLABLE[m]) : undefined);
  const hint = typeof onDrilldown === 'function' ? 'View rows →' : undefined;
  const { asOf, leadGen = {}, leadSources = [], pipeline = {}, revenue = {}, forecast = {}, conversion = {}, activity = {} } = data;
  const hasPlaceholders = !revenue.collectedTracked || !revenue.depositsTracked || !activity.channelBreakdownAvailable;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#212529' }}>CEO Dashboard</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>as of {asOf ? new Date(asOf).toLocaleString('en-US') : '—'}</span>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: '#F8F9FA', boxShadow: neuShadow, cursor: loading ? 'default' : 'pointer', fontSize: 13, color: '#495057' }}
          >
            <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Headline KPI row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <KPI icon={Layers} label="Open opportunities" value={num(pipeline.openOpps)} color="#2E8B98" onClick={drill('openOpps')} clickHint={hint} />
        <KPI icon={DollarSign} label="Open pipeline value" value={money(pipeline.openValue)} color="#256F7A" onClick={drill('openValue')} clickHint={hint} />
        {/* Collected cash, live from Stripe. Rendered only when it is a real
            measurement; otherwise the "Not yet tracked" tile below carries it. */}
        {revenue.collectedTracked && (
          <KPI
            icon={TrendingUp}
            label={revenue.collectedTruncated ? 'Collected revenue (Stripe, partial)' : 'Collected revenue (Stripe)'}
            value={moneyIn(revenue.collectedValue, revenue.collectedCurrency)}
            color="#4FA038"
          />
        )}
        {/* ⚠ RELABELLED, not removed. This is crm_opportunities pipeline value,
            which is not revenue and never was — the old "Booked (won) revenue"
            label was the mislabel. The number is unchanged; only the words are
            honest now. Kept on screen because scripts/weekly_report_digest.js
            reports it too, and because collected revenue can legitimately be
            unavailable, in which case this is the only money figure left. */}
        <KPI icon={Target} label="Booked (won) pipeline value" value={money(revenue.bookedWonValue)} color="#7A5CA8" onClick={drill('bookedWon')} clickHint={hint} />
        <KPI icon={Target} label="Therapies scheduled" value={num(pipeline.therapiesScheduled)} color="#F26522" />
        <KPI icon={Activity} label="Interactions (30d)" value={num(activity.interactions30d)} color="#256F7A" />
      </div>

      {/* ⚠ A capped total must announce itself. The server has always returned
          `truncated` for its Stripe aggregates and no UI in this repo has ever
          read it (src/pages/StripeReportsPage.js reads none of the seven), so a
          2,001-record account renders a silently-wrong total. Not here. */}
      {revenue.collectedTracked && revenue.collectedTruncated && (
        <div
          data-testid="collected-truncated-warning"
          style={{ ...neuCard, marginBottom: 16, borderLeft: '3px solid #F26522', fontSize: 12, color: '#6C757D', lineHeight: 1.5 }}
        >
          <strong style={{ color: '#F26522' }}>Collected revenue is a floor, not a total.</strong>{' '}
          The Stripe read stopped at its pagination bound after {num(revenue.collectedChargeCount)} settled
          charges, so the real figure is higher. Treat this as a lower bound, not a measurement.
        </div>
      )}

      {/* Multiple currencies are never summed. Show them, so the headline figure
          is not mistaken for the whole picture. */}
      {revenue.collectedTracked && revenue.collectedCurrencyCount > 1 && (
        <div
          data-testid="collected-multicurrency"
          style={{ ...neuCard, marginBottom: 16, fontSize: 12, color: '#6C757D', lineHeight: 1.5 }}
        >
          <Info size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
          Collected in {revenue.collectedCurrencyCount} currencies, shown separately and never
          added together:{' '}
          {(revenue.collectedByCurrency || []).map((b, i) => (
            <span key={b.currency}>
              {i > 0 && ' · '}
              <strong>{moneyIn(b.amount, b.currency)}</strong> ({num(b.charges)})
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Lead generation */}
        <div style={{ ...neuCard, flex: '1 1 240px', minWidth: 220, marginBottom: 0 }}>
          <div style={sectionTitle}><Users size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Lead generation</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div><div style={subLabel}>Today</div><div style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{num(leadGen.today)}</div></div>
            <div><div style={subLabel}>This week</div><div style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{num(leadGen.thisWeek)}</div></div>
            <div><div style={subLabel}>This month</div><div style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{num(leadGen.thisMonth)}</div></div>
          </div>
        </div>

        {/* Weighted forecast */}
        <div style={{ ...neuCard, flex: '1 1 240px', minWidth: 220, marginBottom: 0 }}>
          <div style={sectionTitle}><TrendingUp size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Weighted forecast</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={forecastCell}><div style={subLabel}>30 days</div><div style={bigMoney}>{money(forecast.d30)}</div></div>
            <div style={forecastCell}><div style={subLabel}>60 days</div><div style={bigMoney}>{money(forecast.d60)}</div></div>
            <div style={forecastCell}><div style={subLabel}>90 days</div><div style={bigMoney}>{money(forecast.d90)}</div></div>
          </div>
        </div>

        {/* Conversion */}
        <div style={{ ...neuCard, flex: '1 1 240px', minWidth: 220, marginBottom: 0 }}>
          <div style={sectionTitle}><Target size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Conversion</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div><div style={subLabel}>Lead → Deposit</div><div style={{ fontSize: 20, fontWeight: 700, color: '#2E8B98' }}>{conversion.leadToDeposit ?? 0}%</div></div>
            <div><div style={subLabel}>Lead → Therapy</div><div style={{ fontSize: 20, fontWeight: 700, color: '#2E8B98' }}>{conversion.leadToTherapy ?? 0}%</div></div>
          </div>
          {conversion.approximated && (
            <div style={{ fontSize: 11, color: '#B45309', marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Approximate before {fmtDate(conversion.trackingStart)} — pre-tracking leads show their current stage as furthest reached.</span>
            </div>
          )}
        </div>
      </div>

      {/* Lead sources */}
      <div style={{ ...cardWrap, padding: 16, marginBottom: 16 }}>
        <div style={sectionTitle}>Top lead sources (this month)</div>
        {leadSources.length === 0 ? (
          <div style={{ fontSize: 13, color: '#ADB5BD' }}>No sourced leads this month.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {leadSources.map((s) => (
                <tr key={s.source} style={{ borderTop: '1px solid #EEF1F4' }}>
                  <td style={{ padding: '6px 8px', color: '#212529' }}>{s.source}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#495057' }}>{num(s.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Not-yet-tracked placeholders (explicit, never $0). Whole section hidden
          once every source is tracked. */}
      {hasPlaceholders && (
      <div style={{ marginBottom: 4 }}>
        <div style={sectionTitle}>Not yet tracked</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {!revenue.collectedTracked && (
            <NotTracked
              title="Collected revenue"
              reason={COLLECTED_REASON[revenue.collectedUntrackedReason]
                || 'Collected cash is processed in Stripe (external) and is not mirrored locally.'}
            />
          )}
          {!revenue.depositsTracked && (
            <NotTracked title="Deposits pending" reason="No deposit/invoice records captured yet — Stripe external." />
          )}
          {!activity.channelBreakdownAvailable && (
            <NotTracked title="Sales activity by channel" reason="Channel logging not yet in place — all interactions are recorded as a single 'other' type." />
          )}
        </div>
      </div>
      )}
    </div>
  );
}
